// Used with JSON.stringify to prevent circular reference error (ie. JSON.stringify(foo, censor(foo)) )
import { Mutable } from 'type-fest';

/* eslint-disable @typescript-eslint/no-explicit-any */
export const censor: Function = (censoredData: any): any => {
  const cache: any[] = [];

  return (key: any, value: any): any => {
    if (typeof censoredData === 'object' && typeof value === 'object') {
      if (cache.indexOf(value) !== -1) {
        return '[Circular]';
      }
      cache.push(value);
    }
    return value;
  };
};

export const isUndefined: any = (value: any): boolean => value === undefined || value === null; // eslint-disable-line no-null/no-null, max-len
export const isDefined: any = (value: any): boolean => value !== undefined && value !== null; // eslint-disable-line no-null/no-null, max-len
export const isEmpty: any = (obj: any): boolean =>
  (((typeof obj === 'string' || obj instanceof Array) && obj.length === 0) || Object.keys(obj)?.length === 0);
export const isUndefinedOrEmpty: any = (value: any): boolean =>
  isUndefined(value) ||
  ((typeof value === 'string' || value instanceof Array) && value.length === 0) ||
  (typeof value === 'number' && isNaN(value));
export const isDefinedNotEmpty: any = (value: string | Array<any> | number): boolean =>
  isDefined(value) && (
    (typeof value === 'number' && !isNaN(value)) ||
    ((typeof value === 'string' || value instanceof Array) && value.length > 0)
  );
export const isDefinedDateOrStringNotEmpty: any = (value: string | Date): boolean => isDefined(value) &&
  (value instanceof Date || value.length > 0);
/**
 * Performs a deep clone of an object.
 * https://javascript.plainenglish.io/deep-clone-an-object-and-preserve-its-type-with-typescript-d488c35e5574
 *
 * @param source The object to clone.
 * @param preservePropertyDescriptor Whether to preserve the property descriptor. To see what this contains, refer to:
 * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Object/getOwnPropertyDescriptor
 * @param excludedProperties A string or array of strings representing property names you do not want to copy into (at
 * any level) of the cloned object.
 *
 * @returns string A Mutable version of the source object.
 */
export const deepCopy: Function = <T>(source: T, preservePropertyDescriptor: boolean = false,
                                      excludedProperties: string[]): Mutable<T> => { // eslint-disable-line arrow-body-style, max-len
  if (isUndefined(source)) {
    return undefined;
  }

  return Array.isArray(source)
    ? source.map((item: T): Mutable<T> => deepCopy(item, preservePropertyDescriptor, excludedProperties))
    : source instanceof Set
      ? new Set(source) // This works b/c Sets must contain primitives to be useful
      : source instanceof Date
        ? new Date(source.getTime())
        : source && typeof source === 'object'
          ? Object.getOwnPropertyNames(source).reduce((obj: any, prop: string): Mutable<T> => { // eslint-disable-line @typescript-eslint/no-explicit-any, max-len
            if (excludedProperties?.includes(prop)) { // skip property
              return obj;
            } else {
              if (preservePropertyDescriptor) {
                Object.defineProperty(obj, prop, Object.getOwnPropertyDescriptor(source, prop));
              }
              // @ts-ignore: To access the object property with a variable
              obj[prop] = deepCopy(source[prop], preservePropertyDescriptor, excludedProperties);
              return obj;
            }
          }, Object.create(Object.getPrototypeOf(source)))
          : source as Mutable<T>;
};

/**
 * Returns an enum key as a string given an enum value.
 *
 * @param myEnum The enum name
 * @param enumValue A value of myEnum
 */
export const getEnumKeyByEnumValue: Function =
  <R extends (string | number), T extends {[key: string]: R}>(myEnum: T, enumValue: T[keyof T]): string => {
    const keys: string[] = Object.keys(myEnum).filter((x: string): boolean => myEnum[x] === enumValue);
    return keys.length > 0 ? keys[0] : '';
  };

/**
 * Returns an array of enum values given an enum and disallowed values.
 *
 * @param myEnum The enum name.
 * @param disallowedValues An array of values of myEnum that should not be returned.
 *
 * @return An array of all myEnum values without the disallowedValues.
 */
export const numericEnumFilterOut: Function =
  <R extends number, T extends {[key: string]: R}>(myEnum: T, disallowedValues: R[]): R[] =>
    Object.entries(myEnum)
      .filter((type: [string, string | R]): boolean =>
        typeof type[1] === 'number' && !disallowedValues.includes(type[1]))
      .map((type: [string, R]): [string, R] => type as [string, R])
      .map(([, value]: [string, R]): R => value);
